import type {
  AssistantMessage,
  BotPreferences,
  HelpContextSnippet,
  UpdateBotPreferencesRequest,
} from '@brandschutz/shared';
import { api, getAccessToken } from '@/lib/api';

/**
 * API-Wrapper für den Companion-Bot.
 *
 * Streaming: wir nutzen `fetch` mit `Authorization`-Header, weil
 * `EventSource` keine Bearer-Tokens unterstützt. Der Backend-Endpoint
 * sendet text/event-stream; wir parsen das Format manuell.
 */

export async function getBotPreferences(): Promise<BotPreferences> {
  const { data } = await api.get<BotPreferences>('/ai/assistant/preferences');
  return data;
}

export async function updateBotPreferences(
  patch: UpdateBotPreferencesRequest,
): Promise<BotPreferences> {
  const { data } = await api.patch<BotPreferences>('/ai/assistant/preferences', patch);
  return data;
}

export interface StreamCallbacks {
  onDelta: (delta: string) => void;
  onDone: (usage?: { promptTokens: number; completionTokens: number }) => void;
  onError: (message: string) => void;
}

/**
 * Öffnet einen Chat-Stream. Liefert einen AbortController zurück, mit dem
 * der Caller den Stream beenden kann (z. B. wenn der User das Panel
 * schließt oder eine neue Frage stellt, bevor die letzte fertig ist).
 */
/**
 * Wiki-RAG: client-seitige Top-K-Suche über den vollen Hilfe-Index (statisch
 * + auto-generiert). Liefert kompakte Snippets, die ans Backend weitergereicht
 * werden — sodass der Assistant Wiki-Inhalte zitieren kann, ohne sie zu
 * halluzinieren. Suche auf der letzten User-Message.
 *
 * Lunr-Index + Markdown-Kapitel werden dynamisch importiert (eigener Chunk),
 * erst bei der ersten Chat-Nachricht — sonst landen ~100 KB Lunr + der
 * komplette Hilfe-Content im Entry-Bundle (BotBubble hängt statisch in
 * AppShell und zieht dieses Modul mit).
 */
async function collectHelpContext(messages: AssistantMessage[]): Promise<HelpContextSnippet[]> {
  const lastUser = [...messages].reverse().find((m) => m.role === 'user');
  if (!lastUser) return [];
  let searchModule: typeof import('@/skills/help-center/search');
  let contentModule: typeof import('@/skills/help-center/content');
  try {
    [searchModule, contentModule] = await Promise.all([
      import('@/skills/help-center/search'),
      import('@/skills/help-center/content'),
    ]);
  } catch {
    // Wiki-Kontext ist optional: lädt der Chunk nicht (z. B. Stale-Deploy),
    // antwortet der Bot ohne Wiki-Zitate, statt den Chat scheitern zu lassen.
    return [];
  }
  const hits = searchModule.searchHelp(lastUser.content).slice(0, 3);
  const snippets: HelpContextSnippet[] = [];
  for (const h of hits) {
    const ch = contentModule.findChapter(h.slug);
    if (!ch) continue;
    // Markdown auf reinen Text reduzieren und auf 1500 Zeichen kürzen,
    // damit der System-Prompt nicht explodiert.
    const plain = ch.markdown
      .replace(/^#+\s+/gm, '')
      .replace(/[*_`>|]/g, ' ')
      .replace(/\[(.+?)\]\(.+?\)/g, '$1')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 1500);
    snippets.push({
      slug: h.slug,
      title: h.title.slice(0, 300),
      snippet: plain,
    });
  }
  return snippets;
}

export function streamChat(
  messages: AssistantMessage[],
  projectId: number | undefined,
  cb: StreamCallbacks,
): AbortController {
  const controller = new AbortController();
  const token = getAccessToken();
  const baseUrl = '/api/v1';

  void (async () => {
    try {
      const helpContext = await collectHelpContext(messages);
      const res = await fetch(`${baseUrl}/ai/assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ messages, projectId, helpContext }),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        let msg = `HTTP ${res.status}`;
        try {
          const text = await res.text();
          if (text) {
            try {
              const parsed = JSON.parse(text) as { title?: string; detail?: string };
              msg = parsed.detail ?? parsed.title ?? msg;
            } catch {
              msg = text.slice(0, 200);
            }
          }
        } catch {
          // ignore
        }
        cb.onError(msg);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE-Events sind durch \n\n getrennt. Wir verarbeiten alle
        // vollständigen Events und lassen einen ggf. unvollständigen
        // Rest-Buffer für die nächste Iteration stehen.
        let sepIndex = buffer.indexOf('\n\n');
        while (sepIndex !== -1) {
          const rawEvent = buffer.slice(0, sepIndex);
          buffer = buffer.slice(sepIndex + 2);
          sepIndex = buffer.indexOf('\n\n');

          // Events können mehrere `data: …` Zeilen haben — Spec konform.
          const dataLines = rawEvent
            .split('\n')
            .filter((line) => line.startsWith('data:'))
            .map((line) => line.slice(5).trimStart());

          if (dataLines.length === 0) continue;
          const dataStr = dataLines.join('\n');
          try {
            const parsed = JSON.parse(dataStr) as {
              delta?: string;
              done?: boolean;
              usage?: { promptTokens: number; completionTokens: number };
              error?: string;
            };
            if (parsed.error) {
              cb.onError(parsed.error);
              return;
            }
            if (parsed.delta) {
              cb.onDelta(parsed.delta);
            }
            if (parsed.done) {
              cb.onDone(parsed.usage);
              return;
            }
          } catch {
            // Malformed event — überspringen.
          }
        }
      }

      // Stream zu Ende, ohne explizites done-Event — als ok werten.
      cb.onDone(undefined);
    } catch (err) {
      if (controller.signal.aborted) return;
      const msg = err instanceof Error ? err.message : 'Unbekannter Fehler';
      cb.onError(msg);
    }
  })();

  return controller;
}
